import { useState, useEffect, ReactNode } from 'react'
import { useHistory, useLocation } from 'react-router'
import { Formik, Form, FormikHelpers } from 'formik'
import {
  Hidden,
  Box,
  Text,
  Heading,
  Stack,
  useResponsiveValue,
  Inline,
  IconChevronUp,
  IconChevronDown,
  IconArrowLeft,
  TextLink
} from '@machobear/design-system'
import { vars } from '@machobear/design-system/css'
import { useTranslation, Trans } from 'react-i18next'
import styled from 'styled-components'
import client from '@/ports/api-client'
import Skeleton from '@/components/skeleton'
import { ShieldIcon } from '@/components/icons'
import { CouponField, CustomTag } from '@/components/coupon-field'
import formatCurrency from '@/utils/format-currency'
import LogoIcon from './logo.svg?react'
import StripeIcon from '../icons/stripe-icon.svg?react'
import { Product, Item } from '../../types'

type Props = {
  data?: Product
  isLoading?: boolean
  state?: string | null
  promotionId: string
  couponCode?: string | null | undefined
  bundleName: string
}

type SubmitValues = {
  couponCode: string
}

const StyledShieldIcon = styled(ShieldIcon)`
  width: 24px;
  height: 24px;
`

const PaymentSummary = ({ data, isLoading, state, promotionId, couponCode, bundleName }: Props) => {
  const { t } = useTranslation()
  const responsiveValue = useResponsiveValue()
  const history = useHistory()
  const location = useLocation()
  const isMobile = responsiveValue({
    mobile: true,
    tablet: false
  })

  const [showAll, setShowAll] = useState(false)
  const handleList = () => setShowAll(!showAll)
  const paymentbundleName = data?.metadata?.package_name

  useEffect(() => {
    if (!isMobile || (data?.items && data.items.length <= 3)) return setShowAll(true)
  }, [data, isMobile])

  const Wrapper = ({ children }: { children: ReactNode }) =>
    isMobile ? (
      <Box paddingX={{ mobile: 'gutter', tablet: 'gutter' }}>{children}</Box>
    ) : (
      <Inline align="left" space="none">
        <Box display="flex" alignItems="flexStart" width="full">
          {children}
        </Box>
      </Inline>
    )

  const onCouponSubmit = async (
    values: SubmitValues,
    { setSubmitting, setFieldError }: FormikHelpers<SubmitValues>
  ) => {
    setSubmitting(true)

    const response = await client.get(
      `/catalogs/user/packages/CA/${state}/${promotionId}?couponCode=${values.couponCode}`,
      {
        noLoading: true
      }
    )

    if (!response?.products?.[0]?.discounts?.[0]?.isValid) {
      setSubmitting(false)
      setFieldError(
        'couponCode',
        t('l.promotions.coupon.invalid.text', 'Invalid or expired coupon code')
      )
      return
    }
    return history.replace(`${location.pathname}?couponCode=${values.couponCode}`)
  }

  const handleDeleteCoupon = () => {
    history.replace(`${location.pathname}`)
  }

  const hasHistory = history.length > 1

  return (
    <Box
      background="neutralLight"
      padding="large"
      width="full"
      paddingY={isMobile ? 'large' : 'xxxxlarge'}
      paddingX={{ mobile: 'none', tablet: 'gutter', desktop: 'gutter' }}
      style={{
        height: isMobile ? 'unset' : `100vh`,
        borderRight: `1px solid ${vars.borderColor.neutralLight}`,
        display: 'flex',
        justifyContent: isMobile ? 'center' : 'right',
        ...(!isMobile ? { paddingRight: '80px' } : {})
      }}
    >
      <Wrapper>
        <Box>
          {!isMobile && hasHistory && (
            <Box
              onClick={history.goBack}
              display="flex"
              alignItems="center"
              marginTop="medium"
              marginBottom="large"
              style={{
                cursor: 'pointer',
                marginTop: '0px',
                paddingRight: '40px'
              }}
            >
              <Box marginRight="xxsmall">
                <IconArrowLeft />
              </Box>
              <Text weight="strong">{t('common.back', 'Back')}</Text>
            </Box>
          )}
        </Box>
        <Box style={{ ...(!isMobile ? { maxWidth: '425px' } : {}) }}>
          <Stack space={{ mobile: 'large', desktop: 'xxlarge' }}>
            <Hidden below="tablet">
              <Box display="flex" alignItems="center">
                <Box gap="xxsmall" display="flex" paddingBottom="small">
                  <LogoIcon />
                  <StyledShieldIcon />
                </Box>
              </Box>
            </Hidden>

            <Stack space="medium">
              {isLoading ? (
                <Box role="progressbar">
                  <Skeleton width={isMobile ? '325' : '425'} height="32" />
                </Box>
              ) : (
                <>
                  <Heading level="6" weight="strong">
                    <Trans
                      i18nKey="l.promotions.promotion_bundle_name.title"
                      paymentbundleName={paymentbundleName}
                    >
                      Limited time savings on
                    </Trans>
                  </Heading>
                  <Heading level="6" weight="strong">
                    {bundleName}
                  </Heading>
                </>
              )}

              <Text size="small" tone="tertiary">
                {t(
                  'l.promotions.promotion_bundle_description.text',
                  `Don't miss your chance to unlock premium features at our lowest rates of the year.`
                )}
              </Text>
            </Stack>
            <Box>
              <Box paddingBottom="medium" borderBottom="borderNeutralLight">
                <Stack space="medium">
                  {data?.items?.map(
                    (item: Item, i) =>
                      (showAll || (!showAll && i < 3)) && (
                        <Box display="flex" justifyContent="spaceBetween" key={item.item_name}>
                          {isLoading ? (
                            <Skeleton width="329" height="24" />
                          ) : (
                            <Box display="flex" justifyContent="flexStart">
                              <Text size="medium" weight="regular" tone="secondary">
                                {item?.quantity} x
                              </Text>
                              <Box
                                display="flex"
                                flexDirection="column"
                                justifyContent="flexStart"
                                marginLeft="xxxsmall"
                                gap="xsmall"
                                style={{ maxWidth: isMobile ? '184px' : '232px' }}
                              >
                                <Text size="medium" weight="regular" tone="secondary">
                                  {item?.item_name ?? item?.name}
                                </Text>
                                {item?.metadata?.item_description && (
                                  <Text size="small" weight="regular" tone="positive">
                                    {`${isMobile ? '(' : ''}${item?.metadata?.item_description}${
                                      isMobile ? ')' : ''
                                    }`}
                                  </Text>
                                )}
                              </Box>
                            </Box>
                          )}

                          {isLoading ? (
                            <Skeleton width="80" height="24" />
                          ) : (
                            <Inline space="xxxsmall" alignY="center" align="right">
                              {item?.metadata?.current_regular_price ? (
                                <Text size="small" weight="regular" tone="tertiary">
                                  <Box component="s">
                                    {formatCurrency(item.metadata.current_regular_price)}
                                  </Box>
                                </Text>
                              ) : null}
                              <Text size="medium" weight="medium">
                                {formatCurrency(item.value)}
                              </Text>
                            </Inline>
                          )}
                        </Box>
                      )
                  )}
                  {data?.items && data?.items?.length > 3 && isMobile && (
                    <Inline align="left" space="none">
                      <Box onClick={handleList}>
                        <Inline alignY="center" space="small">
                          <Text size="medium" weight="strong" tone="link">
                            {showAll ? 'Show less' : `Show all ${data?.items?.length} items`}
                          </Text>
                          {showAll ? (
                            <IconChevronUp size="small" tone="link" />
                          ) : (
                            <IconChevronDown size="small" tone="link" />
                          )}
                        </Inline>
                      </Box>
                    </Inline>
                  )}
                </Stack>
              </Box>

              <Box paddingY="medium" borderBottom="borderField">
                <Stack space="small">
                  <Box display="flex" justifyContent="spaceBetween">
                    <Text size="medium" weight="regular">
                      {t('r.payment.edit.subtotal.label', 'Subtotal')}
                    </Text>
                    <Text size="medium" weight="medium">
                      {isLoading ? (
                        <Skeleton width="80" height="24" />
                      ) : (
                        `CA${formatCurrency(
                          data?.total_excluding_discount ??
                            data?.regular_price ??
                            data?.total_before_tax ??
                            data?.total_excluding_tax
                        )}`
                      )}
                    </Text>
                  </Box>
                  <Box>
                    {couponCode ? (
                      <Box display="flex" justifyContent="spaceBetween" alignItems="center">
                        <Inline space="xxsmall" alignY="center">
                          <Text size="small" weight="regular">
                            {t('r.payment.edit.discount.label', 'Discount')}
                          </Text>
                          {isLoading ? (
                            <Skeleton width="80" height="24" />
                          ) : (
                            <CustomTag name={couponCode} onClear={handleDeleteCoupon} />
                          )}
                        </Inline>
                        <Text size="small" weight="medium">
                          {isLoading ? (
                            <Skeleton width="80" height="24" />
                          ) : (
                            `-${formatCurrency(
                              data?.discount_from_regular_price ?? data?.discount
                            )}`
                          )}
                        </Text>
                      </Box>
                    ) : (
                      <Formik initialValues={{ couponCode: '' }} onSubmit={onCouponSubmit}>
                        {({ isSubmitting }) => (
                          <Form>
                            <CouponField isSubmitting={isSubmitting} couponCode={couponCode} />
                          </Form>
                        )}
                      </Formik>
                    )}
                  </Box>
                  <Box display="flex" justifyContent="spaceBetween">
                    <Text size="xsmall" weight="regular" tone="tertiary">
                      GST/HST
                    </Text>
                    <Text size="xsmall" weight="regular" tone="tertiary">
                      {isLoading ? (
                        <Skeleton width="80" height="24" />
                      ) : state ? (
                        formatCurrency(data?.total_tax ?? data?.tax)
                      ) : (
                        '-'
                      )}
                    </Text>
                  </Box>
                </Stack>
              </Box>
              <Box display="flex" justifyContent="spaceBetween" marginTop="medium">
                <Text size="large" weight="strong">
                  {t('l.promotions.total.text', 'Total')}
                </Text>
                <Text size="large" weight="strong">
                  {isLoading ? (
                    <Skeleton width="200" height="24" />
                  ) : (
                    `CA${formatCurrency(
                      state
                        ? (data?.total_due ?? data?.total)
                        : (data?.total_before_tax ?? data?.total)
                    )}`
                  )}
                </Text>
              </Box>
            </Box>
            <Hidden below="tablet">
              <Box display="flex" justifyContent="spaceBetween" gap="xxsmall">
                <Text tone="tertiary" size="small">
                  <Trans i18nKey="l.checkout_modal.purchase_summary.help">
                    Got questions?
                    <TextLink
                      target="_blank"
                      rel="noopener noreferrer"
                      href="mailto:support@liv.rent"
                    >
                      Contact us
                    </TextLink>
                  </Trans>
                </Text>
                <Box display="flex">
                  <Box marginRight="xxsmall">
                    <Text size="small" weight="medium" tone="disabled">
                      {t('l.promotions.powered_by.text', 'Powered by')}
                    </Text>
                  </Box>
                  <StripeIcon />
                </Box>
              </Box>
            </Hidden>
          </Stack>
        </Box>
      </Wrapper>
    </Box>
  )
}

export default PaymentSummary
